import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const useCasesOverviewLinks = [
  { title: 'Use cases explained', href: '#use-cases-explained' },
  { title: 'Getting started with AI employees', href: '#getting-started-ai' },
  { title: 'Choosing the right AI employee for your task', href: '#choosing-ai-employee' },
  { title: 'Maximizing productivity with SweetPilot', href: '#maximize-productivity' },
];

const websiteBuilderLinks = [
  { title: 'How to build a website with AI', href: '#build-website' },
  { title: 'Choosing website templates', href: '#website-templates' },
  { title: 'Customizing your website design', href: '#customize-design' },
  { title: 'Adding pages and content', href: '#add-pages' },
  { title: 'Publishing and hosting your website', href: '#publish-website' },
];

const socialMediaLinks = [
  { title: 'How to manage posts on Social Media', href: '#manage-social-posts' },
  { title: 'Creating engaging social content', href: '#create-social-content' },
  { title: 'Scheduling posts for optimal times', href: '#schedule-posts' },
  { title: 'Analyzing social media performance', href: '#analyze-performance' },
  { title: 'Managing multiple social accounts', href: '#multiple-accounts' },
];

const emailManagementLinks = [
  { title: 'How to manage emails', href: '#manage-emails' },
  { title: 'Writing professional emails with AI', href: '#write-professional-emails' },
  { title: 'Email organization and filtering', href: '#email-organization' },
  { title: 'Automating email responses', href: '#automate-responses' },
  { title: 'Email templates and snippets', href: '#email-templates' },
];

const calendarManagementLinks = [
  { title: 'How to manage your calendar', href: '#manage-calendar' },
  { title: 'Scheduling meetings efficiently', href: '#schedule-meetings' },
  { title: 'Setting up recurring events', href: '#recurring-events' },
  { title: 'Calendar integrations with other tools', href: '#calendar-integrations' },
  { title: 'Time blocking and productivity', href: '#time-blocking' },
];

const notionLinks = [
  { title: 'How to use Notion with SweetPilot', href: '#use-notion' },
  { title: 'Creating and organizing Notion pages', href: '#notion-pages' },
  { title: 'Building databases in Notion', href: '#notion-databases' },
  { title: 'Notion templates for productivity', href: '#notion-templates' },
  { title: 'Syncing Notion with other integrations', href: '#notion-sync' },
];

const excelDataLinks = [
  { title: 'How to handle Excel and data', href: '#excel-data' },
  { title: 'Importing and exporting spreadsheets', href: '#import-export' },
  { title: 'Data analysis and visualization', href: '#data-visualization' },
  { title: 'Creating charts and reports', href: '#charts-reports' },
  { title: 'Automating spreadsheet tasks', href: '#automate-spreadsheets' },
];

const brandingLinks = [
  { title: 'How to add your logo', href: '#add-logo' },
  { title: 'Brand customization options', href: '#brand-customization' },
  { title: 'Custom domain connection', href: '#custom-domain' },
  { title: 'White-labeling your workspace', href: '#white-labeling' },
  { title: 'Brand guidelines in Brain AI', href: '#brand-guidelines' },
];

const promptingLinks = [
  { title: 'Prompting best practices', href: '#prompting-best-practices' },
  { title: 'Writing effective prompts', href: '#effective-prompts' },
  { title: 'Common prompting mistakes to avoid', href: '#prompting-mistakes' },
  { title: 'Advanced prompting techniques', href: '#advanced-prompting' },
  { title: 'Prompt templates for different tasks', href: '#prompt-templates' },
];

const dataInsightsLinks = [
  { title: 'How to get data insights', href: '#data-insights' },
  { title: 'Understanding analytics dashboards', href: '#analytics-dashboards' },
  { title: 'Data analysis preparation', href: '#data-preparation' },
  { title: 'Generating reports from data', href: '#generate-reports' },
  { title: 'Exporting insights and data', href: '#export-insights' },
];

const specialUseCasesLinks = [
  { title: 'How to generate workout plans', href: '#workout-plans' },
  { title: 'How to respond to user inquiries', href: '#respond-inquiries' },
  { title: 'How to share info with your team', href: '#share-team-info' },
  { title: 'Creating personalized content', href: '#personalized-content' },
  { title: 'Automating repetitive tasks', href: '#automate-tasks' },
];

const advancedUseCasesLinks = [
  { title: 'Building custom AI workflows', href: '#custom-workflows' },
  { title: 'Multi-step task automation', href: '#multi-step-automation' },
  { title: 'Integrating external APIs', href: '#external-apis' },
  { title: 'Advanced Brain AI training', href: '#advanced-brain-ai' },
  { title: 'Enterprise use cases', href: '#enterprise-use-cases' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function HelperSkillsDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Helper Skills (Use Cases)
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Helper Skills (Use Cases)
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Discover all the ways AI employees can help you accomplish your tasks.
        </p>
      </motion.div>

      {/* Use Cases Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {useCasesOverviewLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* Website Builder */}
      <SectionCard
        title="Website Builder"
        links={websiteBuilderLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* Social Media Management */}
      <SectionCard
        title="Social Media Management"
        links={socialMediaLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Email Management */}
      <SectionCard
        title="Email Management"
        links={emailManagementLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* Calendar Management */}
      <SectionCard
        title="Calendar Management"
        links={calendarManagementLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-6" />

      {/* Notion */}
      <SectionCard
        title="Notion"
        links={notionLinks}
        isDark={isDark}
        delay={0.6}
      />

      <div className="h-6" />

      {/* Excel & Data */}
      <SectionCard
        title="Excel & Data Handling"
        links={excelDataLinks}
        isDark={isDark}
        delay={0.7}
      />

      <div className="h-6" />

      {/* Branding & Customization */}
      <SectionCard
        title="Branding & Customization"
        links={brandingLinks}
        isDark={isDark}
        delay={0.8}
      />

      <div className="h-6" />

      {/* Prompting */}
      <SectionCard
        title="Prompting Skills"
        links={promptingLinks}
        isDark={isDark}
        delay={0.9}
      />

      <div className="h-6" />

      {/* Data Insights */}
      <SectionCard
        title="Data & Insights"
        links={dataInsightsLinks}
        isDark={isDark}
        delay={1.0}
      />

      <div className="h-6" />

      {/* Special Use Cases */}
      <SectionCard
        title="Special Use Cases"
        links={specialUseCasesLinks}
        isDark={isDark}
        delay={1.1}
      />

      <div className="h-6" />

      {/* Advanced Use Cases */}
      <SectionCard
        title="Advanced Use Cases"
        links={advancedUseCasesLinks}
        isDark={isDark}
        delay={1.2}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">💡</span>
        </div>
        <a
          href="mailto:support@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          support@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
